import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  reduxForm,
  Field,
  getFormAsyncErrors,
  getFormValues,
} from 'redux-form';
import _ from 'lodash';
import {
  Input,
  InputNumber,
  Button,
  Select,
  Option,
  Radio,
  Label,
  MultiCheck,
  Message,
  DatePicker,
  OptGroup,
  Check,
  NotificationBanner,
} from 'barnyard/components';
import { Row, Col } from 'barnyard/layouts';
import { FadeTransition } from 'barnyard/transitions';
import { messageIntercom, showIntercom, toPhoneNumber } from 'barnyard/utils';
import NoPermissionMessage from '~/app/common/components/messages/NoPermissionMessage';
import RegionSelect from '~/app/corporate/core/containers/RegionSelect';
import SpeciesSelect from '../components/SpeciesSelect';
import { checkValidProfile } from '../actions';
import {
  BUYER_UPDATE_FORM,
  ReasonCategories,
  ReasonDetails,
  Pims,
} from '../constants';
import { isValidString, normalizeStringLength } from './utils';


const classNamePrefix = 'Signup--Form';
const max5LengthNormalizer = normalizeStringLength(5);
const max10LengthNormalizer = normalizeStringLength(10);

const RADIO_OPTIONS = {
  YES: 1,
  NO: 0,
  MAYBE: 2
};

const validate = (values, props) => {
  const errors = {};
  if (!isValidString(values.name)) {
    errors.name = 'Please enter your clinic name';
  }

  if (!isValidString(values.address_one)) {
    errors.address_one = 'Please enter your street address';
  }

  if (!isValidString(values.postalcode) || _.size(values.postalcode) !== 5) {
    errors.postalcode = 'Please enter a valid 5 digit zipcode';
  }

  if (!isValidString(values.city)) {
    errors.city = 'Please enter your city information';
  }

  if (!isValidString(values.state)) {
    errors.state = 'Please enter your state information';
  }

  if (!isValidString(values.local_timezone)) {
    errors.local_timezone = 'Please select the clinic\'s timezone';
  }

  if (!isValidString(values.reason)) {
    errors.reason = 'Please let us know how you heard about Vetcove';
  }

  if (!isValidString(values.reason_detail)) {
    errors.reason_detail = 'Please tell us more about how you heard about us';
  }

  // Do not require PIMS for corporate
  if (props.formType !== 'corporate' && !isValidString(values.pims)) {
    errors.pims = 'Please select a PIMS';
  }

  if (values.pims === 'other' && !isValidString(values.pims_other)) {
    errors.pims_other = 'Please enter a PIMS name';
  }

  if (values.phone_number && _.size(_.toString(values.phone_number)) !== 10) {
    errors.phone_number = 'Please enter a valid 10 digit phone number';
  }

  if (_.isEmpty(values.animals)) {
    errors.animals = 'Please select at least 1 species type';
  }

  if (_.isEmpty(values.practice_types)) {
    errors.practice_types = 'Please select at least 1 practice type';
  }

  if (undefined === values.purchase_group_enabled) {
    errors.purchase_group_enabled = 'Please select an option';
  }

  if (values.purchase_group_enabled === RADIO_OPTIONS.YES && _.isEmpty(values.purchase_groups)) {
    errors.purchase_groups = 'Please select at least 1 purchase group';
  }

  if (undefined === values.corporate_group_enabled) {
    errors.corporate_group_enabled = 'Please select an option';
  }

  if (values.corporate_group_enabled === RADIO_OPTIONS.YES && !values.corporate_group) {
    errors.corporate_group = 'Please select your corporate group';
  }

  if (values.can_view_scripts_tab && !values.emails && !values.fax_numbers) {
    errors.can_view_scripts_tab = (
      'Please specify at least one outbound fax number or outbound email to enable scripts viewing'
    );
  }

  // Do not require number of employees for corporate
  const numberFTEStaff = _.toNumber(values.number_of_fte_staff)
  if (props.formType !== 'corporate' && (!numberFTEStaff || !_.isInteger(numberFTEStaff))) {
    errors.number_of_fte_staff = 'Please enter a whole number. If you have part time employees, you may choose to round up.';
  }
  const numberFTEVets = _.toNumber(values.number_of_fte_vets)
  if (props.formType !== 'corporate' && (!numberFTEVets || !_.isInteger(numberFTEVets))) {
    errors.number_of_fte_vets = 'Please enter a whole number. If you have part time veterinarians, you may choose to round up.';
  }

  if (!props) {
    return errors;
  }

  // require more fields for corporates specifying required fields in ui settings
  if (props.isAcquistionDateRequired && !values.acquisition_date) {
    errors.acquisition_date = 'Please enter when the clinic was acquired';
  }

  if (props.isDivisionRequired && !values.division) {
    errors.division = 'Please enter the division';
  }

  if (props.isGroupRequired && !values.group) {
    errors.group = 'Please enter the group';
  }

  if (props.isCogsCompanyIdRequired && !values.cogs_company_id) {
    errors.cogs_company_id = 'Please enter the COGs company ID';
  }

  if (props.isCorporateLocationIdRequired && !values.location_id) {
    errors.location_id = 'Please enter the Corporate Location ID';
  }

  if (props.isDefaultCostCenterRequired && !values.default_cost_center) {
    errors.default_cost_center = 'Please enter the default cost center';
  }

  if (props.isOpexCompanyIdRequired && !values.opex_company_id) {
    errors.opex_company_id = 'Please enter the Opex Company ID';
  }

  if (props.isRegionRequired && (!values.regions || _.isEqual(values.regions, [0]))) {
    errors.regions = 'Please add regions for the clinic';
  }

  return errors;
};

const asyncValidate = (values, dispatch, props) => {
  const { initialValues } = props;
  // If the user has acknowledged the existing phone number or the phone number is the same as the inital value
  if (
    values.isDisabledAsyncValidate
    || _.size(values.phone_number) !== 10
    || parseInt(_.get(values, 'phone_number'), 10) === _.get(initialValues, 'phone_number')
  ) {
    return Promise.resolve();
  }
  // Check profile and store errors as form async errors
  return checkValidProfile(
    {
      phone_number: values.phone_number
    },
    {
      onError: (error) => {
        // Ignore 429 throttled errors
        if (error.code === 'throttled') {
          return;
        }
        throw error;
      }
    }
  );
};

const optionsWithNone = arr => _.map(arr, elem => (
  <Option key={elem.id} value={elem.id} name={elem.code}>{elem.name || elem.code}</Option>
));

class BuyerInformationForm extends Component {
  constructor(props) {
    super(props);

    const { initialValues } = props;

    const is_pims_other = !_.isEmpty(initialValues.pims_other);

    this.state = {
      is_corporate_group: initialValues.corporate_group_enabled >= RADIO_OPTIONS.YES,
      is_purchase_group: initialValues.purchase_group_enabled >= RADIO_OPTIONS.YES,
      reason_detail_placeholder: 'How specifically?',
      is_pims_other
    };
  }

  componentDidUpdate(prevProps) {
    const {
      initialValues
    } = this.props;
    const {
      initialValues: prevInitialValues
    } = prevProps;

    if (_.isEqual(initialValues, prevInitialValues)) {
      return;
    }

    this.setState({
      is_corporate_group: initialValues.corporate_group_enabled >= RADIO_OPTIONS.YES,
      is_purchase_group: initialValues.purchase_group_enabled >= RADIO_OPTIONS.YES,
    });
  }

  renderCorporateMetaBuyerOptions() {
    const {
      buyerCorporateMetaOptions,
      isDivisionRequired,
      isGroupRequired,
    } = this.props;

    if (_.isEmpty(buyerCorporateMetaOptions)) {
      return null;
    }

    const corporate_meta_option_to_field_params = {
      division_options: {
        name: 'Division',
        field_name: 'division',
        required: isDivisionRequired,
      },
      group_options: {
        name: 'Group',
        field_name: 'group',
        required: isGroupRequired,
      }
    };

    return (
      <Row gutter='md' className={`${classNamePrefix}__row ${classNamePrefix}__borderRow`}>
        {_.map(buyerCorporateMetaOptions, (options, key) => {
          const field_params = _.get(corporate_meta_option_to_field_params, key, key);
          return (
            <Col key={key} xs={12} md={4}>
              <Field
                name={_.get(field_params, 'field_name', key)}
                label={_.get(field_params, 'name', key)}
                format={null}
                useNative={false}
                component={Select}
                asteriskRequired={_.get(field_params, 'required', false)}
              >
                {_.map(options, option => <Option key={option} value={option}>{option}</Option>)}
              </Field>
            </Col>
          );
        })}
      </Row>
    );
  }

  renderPimsFields() {
    const {
      formType,
      formValues,
    } = this.props;
    return (
      <>
        <div style={{ padding: '8px 8px 0' }}>
          <Label>
            Which Practice Information Management System (PIMS) does your practice use, if any?
            <span className='barnyard-BaseInput__asterisk'>*</span>
          </Label>
        </div>
        <Col xs={12} md={6} className='u-paddingTop0'>
          <Field
            name='pims'
            placeholder='PIMS'
            format={null}
            component={Select}
            error={(formType === 'update' && _.isEmpty(_.get(formValues, 'pims'))) && 'Please select a PIMS'}
            useNative={false}
            onChange={value => this.setState({
              is_pims_other: value === 'other'
            })}
          >
            {
              _.map(_.sortBy(Pims, pim => _.toLower(pim.name)), pim => (
                <Option key={pim.key} value={pim.key}>{pim.name}</Option>
              ))
            }
            <Option key='none' value='None'>None</Option>
            <Option key='other' value='other'>Other</Option>
          </Field>
        </Col>
        {this.state.is_pims_other && (
          <Col xs={12} md={6} className='u-paddingTop0'>
            <Field
              name='pims_other'
              placeholder='Other PIMS'
              format={null}
              component={Input}
            />
          </Col>
        )}
      </>
    )
  }

  renderSecondSection() {
    const {
      formType,
      onZipcodeChange,
      buyerFormFields,
      disabledFields,
      hiddenFields,
      corporateCompanies,
      corporateCostCenters,
      isCogsCompanyIdRequired,
      isDefaultCostCenterRequired,
      isOpexCompanyIdRequired,
      isReadOnly,
      formValues,
    } = this.props;

    if (formType === 'update') {
      return (
        <Row gutter='md' className={`${classNamePrefix}__row ${classNamePrefix}__borderRow`}>
          <Col xs={12} md={6}>
            <Field
              name='name'
              label='Clinic Name'
              placeholder='The Purrfect Clinic'
              component={Input}
              error={!isValidString(_.get(formValues, 'name')) && 'Please enter your clinic name'}
              asteriskRequired={true}
              disabled={isReadOnly}
            />
          </Col>
          <Col xs={12} md={6}>
            <Field
              name='phone_number'
              label='Clinic Phone Number'
              preIcon={['fal', 'phone']}
              placeholder='(212) 867-5309'
              displayFormat='phone'
              component={Input}
              error={
                _.size(_.toString(_.get(formValues, 'phone_number'))) !== 10 && (
                  'Please enter a valid 10 digit phone number'
                )
              }
              normalize={max10LengthNormalizer}
              asteriskRequired={true}
              disabled={isReadOnly}
            />
          </Col>
          {this.renderPimsFields()}
        </Row>
      );
    }

    if (formType === 'corporate') {
      return (
        <>
          <Row gutter='md' className={`${classNamePrefix}__row ${classNamePrefix}__borderRow`}>
            <Col xs={12} md={4}>
              <Field
                name='name'
                label='Clinic Name'
                placeholder='The Purrfect Clinic'
                component={Input}
                asteriskRequired={true}
              />
            </Col>
            <Col xs={12} md={4}>
              <Field
                name='phone_number'
                label='Clinic Phone Number'
                preIcon={['fal', 'phone']}
                placeholder='(212) 867-5309'
                displayFormat='phone'
                component={Input}
                normalize={max10LengthNormalizer}
                asteriskRequired={true}
              />
            </Col>
            <Col xs={12} md={4}>
              <Field
                name='local_timezone'
                label='Clinic Timezone'
                placeholder='Timezone'
                displayFormat='phone'
                component={Select}
                showSearch={true}
                autoComplete='off'
                asteriskRequired={true}
              >
                {
                  _.map(
                    buyerFormFields.timezones,
                    (timezoneName, timezoneKey) => (
                      <Option key={timezoneKey} value={timezoneKey} name={`${timezoneName},${timezoneKey}`}>
                        {timezoneName}
                      </Option>
                    )
                  )
                }
              </Field>
            </Col>
            <Col xs={12} md={8}>
              <Field
                name='address_one'
                label='Clinic Street Address'
                placeholder='Street Address'
                component={Input}
                autoComplete='off'
                asteriskRequired={true}
              />
            </Col>
            <Col xs={12} md={4}>
              <Field
                name='address_two'
                label='Suite'
                placeholder='Suite, Building, .etc'
                component={Input}
                autoComplete='off'
              />
            </Col>
            <Col xs={12} md={4}>
              <Field
                name='postalcode'
                label='Zipcode'
                placeholder='Zipcode'
                component={Input}
                onChange={onZipcodeChange}
                autoComplete='off'
                normalize={max5LengthNormalizer}
                asteriskRequired={true}
              />
            </Col>
            <Col xs={12} md={4}>
              <Field
                name='city'
                label='City'
                placeholder='City'
                component={Input}
                autoComplete='off'
                asteriskRequired={true}
              />
            </Col>
            <Col xs={12} md={4}>
              <Field
                name='state'
                label='State'
                placeholder='State'
                format={null}
                showSearch={true}
                component={Select}
                autoComplete='off'
                optionFilterProp='name'
                asteriskRequired={true}
              >
                {
                  _.map(
                    buyerFormFields.states,
                    (stateName, stateKey) => (
                      <Option key={stateKey} value={stateKey} name={`${stateName},${stateKey}`}>{stateKey}</Option>
                    )
                  )
                }
              </Field>
            </Col>
            {!_.includes(hiddenFields, 'fax_numbers') && (
              <Col xs={12} md={6}>
                <Field
                  name='fax_numbers'
                  tip='Comma-separated list of 10-digits fax numbers'
                  label='Outbound Fax Numbers'
                  component={Input}
                  preIcon={['fal', 'fax']}
                  placeholder='2127680953, 2124567890...'
                  autoComplete='off'
                  disabled={_.includes(disabledFields, 'fax_numbers')}
                />
              </Col>
            )}
            {!_.includes(hiddenFields, 'emails') && (
              <Col xs={12} md={6}>
                <Field
                  name='emails'
                  tip='Comma-separated list of emails'
                  label='Outbound Emails'
                  placeholder='pooch@woof.com, tom@vetcove.com...'
                  component={Input}
                  preIcon={['fal', 'envelope']}
                  autoComplete='off'
                  disabled={_.includes(disabledFields, 'emails')}
                />
              </Col>
            )}
            {!_.includes(hiddenFields, 'corporate_buyer_email') && (
              <Col xs={12} md={6}>
                <Field
                  name='corporate_buyer_email'
                  label='Contact Email'
                  placeholder='pooch@woof.com'
                  component={Input}
                  preIcon={['fal', 'envelope']}
                  autoComplete='off'
                  disabled={_.includes(disabledFields, 'corporate_buyer_email')}
                />
              </Col>
            )}
            {this.renderPimsFields()}
          </Row>
          {this.renderCorporateMetaBuyerOptions()}
          {(!_.isEmpty(corporateCompanies) || !_.isEmpty(corporateCostCenters)) && (
            <Row gutter='md' className={`${classNamePrefix}__row ${classNamePrefix}__borderRow`}>
              {!_.isEmpty(corporateCompanies) && (
                <>
                  <Col xs={12} md={4}>
                    <Field
                      name='cogs_company_id'
                      label='Cogs Company ID'
                      format={null}
                      useNative={false}
                      component={Select}
                      asteriskRequired={isCogsCompanyIdRequired}
                    >
                      <Option key='cogs_none' value={null}>None</Option>
                      {optionsWithNone(corporateCompanies)}
                    </Field>
                  </Col>
                  <Col xs={12} md={4}>
                    <Field
                      name='opex_company_id'
                      label='Opex Company ID'
                      format={null}
                      useNative={false}
                      component={Select}
                      asteriskRequired={isOpexCompanyIdRequired}
                    >
                      <Option key='opex_none' value={null}>None</Option>
                      {optionsWithNone(corporateCompanies)}
                    </Field>
                  </Col>
                </>
              )}
              {!_.isEmpty(corporateCostCenters) && (
                <Col xs={12} md={4}>
                  <Field
                    name='default_cost_center'
                    label='Default Cost Center'
                    format={null}
                    useNative={false}
                    component={Select}
                    asteriskRequired={isDefaultCostCenterRequired}
                  >
                    <Option key='cost_center_none' value={null}>None</Option>
                    {optionsWithNone(corporateCostCenters)}
                  </Field>
                </Col>
              )}
            </Row>
          )}
        </>
      );
    }

    return (
      <React.Fragment>
        <Row gutter='md' className={`${classNamePrefix}__row`}>
          <Col xs={12} md={8}>
            <Field
              name='address_one'
              label='Clinic Street Address'
              placeholder='Street Address'
              component={Input}
              autoComplete='off'
              asteriskRequired={true}
              disabled={isReadOnly}
            />
          </Col>
          <Col xs={12} md={4}>
            <Field
              name='address_two'
              label='Suite'
              placeholder='Suite, Building, .etc'
              component={Input}
              autoComplete='off'
              disabled={isReadOnly}
            />
          </Col>
        </Row>
        <Row gutter='md' className={`${classNamePrefix}__row ${classNamePrefix}__borderRow`}>
          <Col xs={12} md={4}>
            <Field
              name='postalcode'
              label='Zipcode'
              placeholder='Zipcode'
              component={Input}
              onChange={onZipcodeChange}
              autoComplete='off'
              normalize={max5LengthNormalizer}
              asteriskRequired={true}
              disabled={isReadOnly}
            />
          </Col>
          <Col xs={12} md={4}>
            <Field
              name='city'
              label='City'
              placeholder='City'
              component={Input}
              autoComplete='off'
              asteriskRequired={true}
              disabled={isReadOnly}
            />
          </Col>
          <Col xs={12} md={4}>
            <Field
              name='state'
              label='State'
              placeholder='State'
              format={null}
              showSearch={true}
              component={Select}
              autoComplete='off'
              optionFilterProp='name'
              asteriskRequired={true}
              disabled={isReadOnly}
            >
              {
                _.map(
                  buyerFormFields.states,
                  (stateName, stateKey) => (
                    <Option key={stateKey} value={stateKey} name={`${stateName},${stateKey}`}>{stateKey}</Option>
                  )
                )
              }
            </Field>
          </Col>
        </Row>
        <Row gutter='md' className={`${classNamePrefix}__row ${classNamePrefix}__borderRow`}>
          {
            formType === 'signup' ? (
              <>
                <Col xs={12} md={6}>
                  <Field
                    name='reason'
                    label='How did you hear about us'
                    placeholder='Representative, Conference, etc.'
                    format={null}
                    component={Select}
                    useNative={false}
                    onChange={value => this.setState({
                      reason_detail_placeholder: ReasonDetails[value]
                    })}
                    asteriskRequired={true}
                  >
                    {
                      _.map(ReasonCategories.groups, group => (
                        <OptGroup label={group.name} key={group.name}>
                          {
                            _.map(
                              group.key_values,
                              (val, key) => (
                                <Option key={key} value={key}>{val}</Option>
                              )
                            )
                          }
                        </OptGroup>
                      ))
                    }
                  </Field>
                </Col>
                <Col xs={12} md={6}>
                  <Field
                    name='reason_detail'
                    label='How did you hear details'
                    placeholder={this.state.reason_detail_placeholder}
                    format={null}
                    component={Input}
                    asteriskRequired={true}
                  />
                </Col>
                {this.renderPimsFields()}
              </>
            ) : (
              <Col xs={12} md={4}>
                <Field
                  name='phone_number'
                  label='Clinic Phone Number'
                  preIcon={['fal', 'phone']}
                  placeholder='(212) 867-5309'
                  displayFormat='phone'
                  component={Input}
                  autoComplete='off'
                  normalize={max10LengthNormalizer}
                  asteriskRequired={true}
                  disabled={isReadOnly}
                />
              </Col>
            )
          }
        </Row>
      </React.Fragment>
    );
  }

  // eslint-disable-next-line class-methods-use-this
  renderThirdSection() {
    const {
      formType,
      formValues,
    } = this.props;
    return (
      <Row gutter='md' className={`${classNamePrefix}__row ${classNamePrefix}__borderRow`}>
        <Col xs={12} md={6}>
          <Field
            name='number_of_fte_staff'
            label='Number of Full-Time Employees'
            placeholder='Number of Full-Time Employees'
            component={InputNumber}
            error={
              (formType === 'update' && !_.isInteger(_.toNumber(_.get(formValues, 'number_of_fte_staff')))) && (
                'Please enter a whole number. If you have part time employees, you may choose to round up.'
              )
            }
            min={1}
            asteriskRequired={true}
          />
        </Col>
        <Col xs={12} md={6}>
          <Field
            name='number_of_fte_vets'
            label='Number of Full-Time Veterinarians'
            placeholder='Number of Full-Time Veterinarians'
            component={InputNumber}
            error={
              (formType === 'update' && !_.isInteger(_.toNumber(_.get(formValues, 'number_of_fte_vets')))) && (
                'Please enter a whole number. If you have part time veterinarians, you may choose to round up.'
              )
            }
            min={1}
            asteriskRequired={true}
          />
        </Col>
      </Row>
    );
  }

  renderFourthSection() {
    const {
      formType,
      buyerFormFields,
      disabledFields,
      hiddenFields,
      scriptsEnabled,
      showSecondaryLocationField,
      isAcquistionDateRequired,
      isRegionRequired,
      isCorporateLocationIdRequired,
      isReadOnly
    } = this.props;
    const {
      is_corporate_group,
      is_purchase_group,
    } = this.state;

    if (formType === 'corporate') {
      return (
        <Row gutter='md' className={`${classNamePrefix}__row ${classNamePrefix}__borderRow`}>
          {!_.includes(hiddenFields, 'can_sync_with_pims') && (
            <Col xs={12} md={4}>
              <Field
                name='can_sync_with_pims'
                component={Check}
                label='Can Sync PIMS?'
              />
            </Col>
          )}
          {scriptsEnabled && (
            <>
              {!_.includes(hiddenFields, 'can_view_scripts_tab') && (
                <Col xs={12} md={4}>
                  <Field
                    name='can_view_scripts_tab'
                    component={Check}
                    label='Can View Scripts?'
                  />
                </Col>
              )}
              {!_.includes(hiddenFields, 'is_deny_only') && (
                <Col xs={12} md={4}>
                  <Field
                    name='is_deny_only'
                    component={Check}
                    label='Deny All Third-Party Scripts?'
                  />
                </Col>
              )}
            </>
          )}
          <Col xs={12} md={6}>
            <Field
              name='acquisition_date'
              label='Acquisition Date'
              placeholder='Select the date this Clinic was acquired'
              format={null}
              component={DatePicker}
              inputFormat='MM/DD/YYYY'
              asteriskRequired={isAcquistionDateRequired}
            />
          </Col>
          <Col xs={12} md={6}>
            <Field
              name='location_id'
              label='Corporate Location ID'
              placeholder='Location ID'
              component={Input}
              asteriskRequired={isCorporateLocationIdRequired}
            />
          </Col>
          {showSecondaryLocationField && (
            <Col xs={12} md={6}>
              <Field
                name='secondary_location_id'
                label='Secondary Corporate Location ID'
                placeholder='Location ID'
                component={Input}
              />
            </Col>
          )}
          <Col span={12}>
            <Field
              name='regions'
              label='Select regions'
              useNative={false}
              allowClear={true}
              component={props => <RegionSelect defaultDisplay='No Regions' {...props.input} {...props} />}
              asteriskRequired={isRegionRequired}
            >
            </Field>
          </Col>
        </Row>
      );
    }

    return (
      <React.Fragment>
        <Row gutter='md' className={`${classNamePrefix}__row u-marginTop0`} align='middle' justify='edges'>
          <Col xs={12} md={8}>
            <Label>
              Do you belong to a buying group / group purchasing organization (GPO)?
              <span className='barnyard-BaseInput__asterisk'>*</span>
            </Label>
            <p className={`${classNamePrefix}__row__description`}>Examples: PSI, VMG, TVC, VerticalVet, etc.</p>
          </Col>
          <Col xs={12} md={0}>
            <Field
              name='purchase_group_enabled'
              component={Radio.Group}
              buttonStyle='solid'
              onChange={(e, value) => this.setState({
                is_purchase_group: value >= 1
              })}
              disabled={isReadOnly}
            >
              <Radio.Button key={RADIO_OPTIONS.YES} value={RADIO_OPTIONS.YES}>Yes</Radio.Button>
              <Radio.Button key={RADIO_OPTIONS.NO} value={RADIO_OPTIONS.NO}>No</Radio.Button>
              <Radio.Button key={RADIO_OPTIONS.MAYBE} value={RADIO_OPTIONS.MAYBE}>Not Sure</Radio.Button>
            </Field>
          </Col>
        </Row>
        <FadeTransition visible={is_purchase_group}>
          <Row gutter='md' gutterAxis='horizontal' className={`${classNamePrefix}__row`}>
            <Col span={12}>
              <Field
                name='purchase_groups'
                label='Select all that apply'
                options={_.map(
                  buyerFormFields.purchase_groups,
                  (pName, pKind) => ({ label: pName, value: pKind })
                )}
                format={null}
                span={4}
                component={MultiCheck}
                asteriskRequired={true}
                disabled={isReadOnly ? _.map(buyerFormFields.purchase_groups, (pName, pKind) => pKind) : []}
              />
            </Col>
          </Row>
        </FadeTransition>
        <Row gutter='md' className={`${classNamePrefix}__row u-marginTop20`} align='middle' justify='edges'>
          <Col xs={12} md={8}>
            <Label>
              Is your practice owned by a corporate hospital group?
              <span className='barnyard-BaseInput__asterisk'>*</span>
            </Label>
            <p className={`${classNamePrefix}__row__description`}>Examples: VCA, BluePearl, NVA, VetCor, etc.</p>
          </Col>
          <Col xs={12} md={0}>
            <Field
              disabled={_.includes(disabledFields, 'corporate_group_enabled') || isReadOnly}
              name='corporate_group_enabled'
              component={Radio.Group}
              buttonStyle='solid'
              onChange={(e, value) => this.setState({
                is_corporate_group: value >= 1
              })}
            >
              <Radio.Button key={RADIO_OPTIONS.YES} value={RADIO_OPTIONS.YES}>Yes</Radio.Button>
              <Radio.Button key={RADIO_OPTIONS.NO} value={RADIO_OPTIONS.NO}>No</Radio.Button>
              <Radio.Button key={RADIO_OPTIONS.MAYBE} value={RADIO_OPTIONS.MAYBE}>Not Sure</Radio.Button>
            </Field>
          </Col>
        </Row>
        <FadeTransition visible={is_corporate_group}>
          <Row gutter='md' className={`${classNamePrefix}__row`}>
            <Col xs={12} md={8}>
              <Field
                disabled={_.includes(disabledFields, 'corporate_group') || isReadOnly}
                name='corporate_group'
                label='Select your corporate group'
                placeholder='Select your corporate group'
                format={null}
                component={Select}
                showSearch={true}
                optionFilterProp='name'
                asteriskRequired={true}
              >
                {
                  _.map(
                    buyerFormFields.corporate_groups,
                    (cgName, cgKey) => (
                      <Option key={cgKey} value={cgKey} name={cgName}>{cgName}</Option>
                    )
                  )
                }
              </Field>
            </Col>
            <Col xs={12} md={8}>
              <Field
                name='acquisition_date'
                label='Acquisition Date (Planned Acquisition Date)'
                placeholder='Select the date this Clinic was (will be) acquired'
                format={null}
                component={DatePicker}
                inputFormat='MM/DD/YYYY'
                asteriskRequired={true}
                disabled={isReadOnly}
              />
            </Col>
          </Row>
        </FadeTransition>
      </React.Fragment>
    );
  }


  renderButtons() {
    const {
      submitText,
      onCancel,
      submitting,
      formType,
      isReadOnly,
    } = this.props;

    if (formType === 'update' || formType === 'corporate') {
      return (
        <Row className={`${classNamePrefix}__buttons`} gutter='md' justify='edges'>
          <Col span={12}>
            <Button
              className='button'
              color='primary'
              type="submit"
              full={true}
              loading={submitting}
              disabled={isReadOnly}
            >
              {submitText}
            </Button>
          </Col>
        </Row>
      );
    }

    return (
      <Row className={`${classNamePrefix}__buttons`} gutter='md' justify='edges'>
        <Col>
          {
            onCancel ? (
              <Button
                color='danger'
                naked={true}
                onClick={onCancel}
                type='button'
              >
                Cancel
              </Button>
            ) : (
              <Button
                color='primary'
                naked={true}
                onClick={showIntercom}
                type='button'
                className='u-padding0'
              >
                Need Help? Let&apos;s Chat!
              </Button>
            )
          }
        </Col>
        <Col xs={12} md={6}>
          <Button
            className='button'
            color='primary'
            type="submit"
            full={true}
            loading={submitting}
          >
            {submitText}
          </Button>
        </Col>
      </Row>
    );
  }

  rendorDuplicateErrorMessage() {
    const {
      change,
      clearAsyncError,
      formValues,
      aErrors,
    } = this.props;

    if (_.isNil(_.get(aErrors, 'phone_number'))) {
      return null;
    }

    return (
      <NotificationBanner
        className='barnyard-NotifyBanner'
        style={{ bottom: 0, left: 0 }}
        type='danger'
        message={(
          <div className={`${classNamePrefix}__alertContainer`}>
            <p>
              Heads up! It looks like the phone number you&apos;ve entered is already associated with an{' '}
              existing Vetcove account.
            </p>
            <Row justify='center' gutter='sm'>
              <Col className='leftButton' span={{ xs: 12, sm: 6 }}>
                <Button
                  color='naked'
                  size={'xs'}
                  className={`${classNamePrefix}__alertContainer-button`}
                  onClick={(e) => {
                    e.preventDefault();
                    change('isDisabledAsyncValidate', true);
                    clearAsyncError('phone_number');
                  }}
                >
                  Continue
                </Button>
              </Col>
              <Col className='rightButton' span={{ xs: 12, sm: 6 }}>
                <Button
                  size={'xs'}
                  onClick={(e) => {
                    e.preventDefault();
                    messageIntercom(
                      'I am looking to find my existing account with phone number '
                      + `${toPhoneNumber({ number: formValues.phone_number })}`
                    );
                  }}
                >
                  Find Existing Account
                </Button>
              </Col>
            </Row>
          </div>
        )}
      />
    );
  }

  render() {
    const {
      handleSubmit,
      buyerFormFields,
      error,
      isReadOnly,
    } = this.props;

    return (
      <form className={classNamePrefix} onSubmit={handleSubmit}>
        {
          error && (
            <Message
              color='danger'
              border={true}
              message={(
                <React.Fragment>
                  <strong><h4>{error.title}</h4></strong>
                  <p>{error.details}</p>
                </React.Fragment>
              )}
            />
          )
        }
        <NoPermissionMessage hasPermission={!isReadOnly} />
        <Row gutter='md' className={`${classNamePrefix}__row`}>
          <Col span={12}>
            <Field
              name='animals'
              label='Select ALL species treated by your practice'
              speciesTypes={buyerFormFields.animals}
              component={SpeciesSelect}
              format={null}
              asteriskRequired={true}
              disabled={isReadOnly}
            />
          </Col>
        </Row>
        <Row
          gutter='md'
          gutterAxis='horizontal'
          className={`${classNamePrefix}__row ${classNamePrefix}__borderRow u-marginTop20`}
        >
          <Col span={12} className='u-marginTop5'>
            <Field
              name='practice_types'
              label='Practice Types (select ALL that apply)'
              options={_.map(
                buyerFormFields.practice_types,
                (pName, pKind) => ({ label: pName, value: pKind })
              )}
              format={null}
              component={MultiCheck}
              asteriskRequired={true}
              disabled={isReadOnly ? _.map(buyerFormFields.practice_types, (pName, pKind) => pKind) : []}
            />
          </Col>
        </Row>
        {this.renderSecondSection()}
        {this.renderThirdSection()}
        {this.renderFourthSection()}
        {this.renderButtons()}
        {this.rendorDuplicateErrorMessage()}
      </form>
    );
  }
}

BuyerInformationForm.defaultProps = {
  submitText: 'Update Information',
  formType: 'signup',
  scriptsEnabled: false,
  showSecondaryLocationField: false,
  isReadOnly: false,
};

BuyerInformationForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  clearAsyncError: PropTypes.func.isRequired,
  onZipcodeChange: PropTypes.func.isRequired,
  valid: PropTypes.bool.isRequired,
  buyerFormFields: PropTypes.object.isRequired,
  initialValues: PropTypes.object.isRequired,
  error: PropTypes.object,
  submitText: PropTypes.string,
  onCancel: PropTypes.func,
  formType: PropTypes.string,
  submitting: PropTypes.bool,
  disabledFields: PropTypes.array,
  hiddenFields: PropTypes.array,
  corporateCompanies: PropTypes.array,
  corporateCostCenters: PropTypes.array,
  scriptsEnabled: PropTypes.bool,
  showSecondaryLocationField: PropTypes.bool,
  formValues: PropTypes.object,
  aErrors: PropTypes.object,
  isAcquistionDateRequired: PropTypes.bool,
  isCogsCompanyIdRequired: PropTypes.bool,
  isDefaultCostCenterRequired: PropTypes.bool,
  isOpexCompanyIdRequired: PropTypes.bool,
  isRegionRequired: PropTypes.bool,
  isCorporateLocationIdRequired: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  buyerCorporateMetaOptions: PropTypes.object,
  isDivisionRequired: PropTypes.bool,
  isGroupRequired: PropTypes.bool,
};

const mapStateToProps = state => ({
  formValues: getFormValues(BUYER_UPDATE_FORM)(state),
  aErrors: getFormAsyncErrors(BUYER_UPDATE_FORM)(state),
});

export default connect(mapStateToProps)(reduxForm({
  form: BUYER_UPDATE_FORM,
  validate,
  asyncValidate,
  asyncBlurFields: ['phone_number']
})(BuyerInformationForm));
